// src/utils/errorHandlers.ts
//
// Global error handling for Tradeatory.
//
// `errorReporter` is the SINGLE place the app talks to when it wants to
// report a caught error. Today it ring-buffers locally + console.errors;
// M-C will replace `captureException` with `Sentry.captureException(...)`
// so the rest of the app doesn't have to know about Sentry.
//
// Integration points:
//   • Global window error listener (registered in init())
//   • Unhandled promise rejection listener (init())
//   • Resource loading error listener (init())
//   • React error boundaries — GlobalErrorBoundary.componentDidCatch
//     calls `errorReporter.captureException(error, { componentStack })`
//     so caught React errors flow through the same pipeline.

import { logger } from './logger';

export interface ErrorReport {
  message: string;
  stack?: string;
  url?: string;
  lineNumber?: number;
  columnNumber?: number;
  timestamp: Date;
  userAgent: string;
  type: 'javascript' | 'promise' | 'resource';
}

// Extra context the caller can attach to a captured exception. Mirrors the
// shape Sentry expects so the M-C swap is a one-liner.
export interface ErrorContext {
  tags?: Record<string, string>;
  extra?: Record<string, unknown>;
  // React error boundaries pass this from ErrorInfo.componentStack.
  componentStack?: string;
  // Logical surface that caught the error — used as a Sentry tag.
  feature?: string;
}

class ErrorReporter {
  private errors: ErrorReport[] = [];
  private maxErrors = 50;
  // M-C will replace this with a Sentry-backed implementation. The signature
  // is stable so call sites don't change.
  private exceptionSink: ((error: unknown, context?: ErrorContext) => void) | null = null;

  // M-C entry point: register a Sentry-backed sink. Until M-C lands, the
  // null sink causes captureException to fall back to the local console
  // path. Idempotent — calling twice replaces the previous sink.
  public registerExceptionSink(sink: (error: unknown, context?: ErrorContext) => void): void {
    this.exceptionSink = sink;
  }

  // Single entry point for reporting a caught exception. Call this from
  // error boundaries, async handlers, or anywhere a try/catch wants to
  // surface the failure to monitoring without crashing.
  public captureException(error: unknown, context?: ErrorContext): void {
    if (this.exceptionSink) {
      try {
        this.exceptionSink(error, context);
      } catch (sinkErr) {
        // Sentry should never throw, but defensive: don't let the reporter
        // itself become the source of a cascade.
        logger.error('[errorReporter] sink threw:', sinkErr);
      }
    }
    // Always also record locally — useful for in-browser debug + the dev
    // ErrorOverlay component reads from this buffer.
    const err = error instanceof Error ? error : new Error(String(error));
    this.reportError({
      message: err.message + (context?.feature ? ` [feature=${context.feature}]` : ''),
      stack: err.stack,
      timestamp: new Date(),
      userAgent: navigator.userAgent,
      type: 'javascript',
    });
  }

  public init() {
    // Global JavaScript error handler
    window.addEventListener('error', (event) => {
      this.reportError({
        message: event.message,
        stack: event.error?.stack,
        url: event.filename,
        lineNumber: event.lineno,
        columnNumber: event.colno,
        timestamp: new Date(),
        userAgent: navigator.userAgent,
        type: 'javascript'
      });
    });

    // Unhandled Promise rejection handler
    window.addEventListener('unhandledrejection', (event) => {
      this.reportError({
        message: `Unhandled Promise Rejection: ${event.reason}`,
        stack: event.reason?.stack,
        timestamp: new Date(),
        userAgent: navigator.userAgent,
        type: 'promise'
      });
    });

    // Resource loading error handler
    window.addEventListener('error', (event) => {
      if (event.target !== window) {
        const t = event.target as HTMLElement & { src?: string; href?: string };
        this.reportError({
          message: `Resource failed to load: ${t?.src || t?.href || '(unknown)'}`,
          timestamp: new Date(),
          userAgent: navigator.userAgent,
          type: 'resource',
        });
      }
    }, true);

    logger.debug('🛡️ Global error handlers initialized');
  }

  private reportError(error: ErrorReport) {
    // Add to internal log
    this.errors.unshift(error);
    if (this.errors.length > this.maxErrors) {
      this.errors = this.errors.slice(0, this.maxErrors);
    }

    // Enhanced console logging — always logs (logger.error path) so prod
    // can still capture via Sentry's console integration when M-C lands.
    logger.error(
      `[${error.type.toUpperCase()} ERROR] ${error.message}`,
      {
        stack: error.stack,
        file: error.url ? `${error.url}:${error.lineNumber}:${error.columnNumber}` : undefined,
        at: error.timestamp.toISOString(),
      },
    );

    // Show toast notification in development. Uses the Vite-native env so it
    // strips at build time instead of relying on `process.env.NODE_ENV`.
    if (import.meta.env.DEV) {
      this.showErrorToast(error);
    }
  }

  private showErrorToast(error: ErrorReport) {
    // Create a temporary toast notification
    const toast = document.createElement('div');
    toast.className = 'fixed top-4 right-4 bg-red-600 text-white p-4 rounded-lg shadow-lg z-50 max-w-sm';
    toast.innerHTML = `
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
          </svg>
        </div>
        <div class="ml-3">
          <div class="text-sm font-medium">Runtime Error</div>
          <div class="text-xs mt-1 opacity-90">${error.message.slice(0, 100).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')}${error.message.length > 100 ? '...' : ''}</div>
        </div>
        <button class="ml-4 text-white hover:text-red-200" onclick="this.parentElement.parentElement.remove()">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
          </svg>
        </button>
      </div>
    `;

    document.body.appendChild(toast);

    // Auto-remove after 5 seconds
    setTimeout(() => {
      if (toast.parentElement) {
        toast.remove();
      }
    }, 5000);
  }

  public getErrors(): ErrorReport[] {
    return [...this.errors];
  }

  public clearErrors() {
    this.errors = [];
  }
}

export const errorReporter = new ErrorReporter();

// React Error Helper — kept for any direct callers; new code should use
// errorReporter.captureException(error, { componentStack }) instead, which
// routes through the Sentry sink once it's registered.
export const logReactError = (error: Error, errorInfo: { componentStack?: string }) => {
  errorReporter.captureException(error, {
    componentStack: errorInfo.componentStack,
    feature: 'react-error-boundary',
  });
};

// Development helpers — attach __SHOW_ERRORS__ / __CLEAR_ERRORS__ in DEV
// for grabbing the local ring buffer from the devtools console.
export const enableErrorDebugging = () => {
  if (import.meta.env.DEV) {
    const w = window as unknown as Record<string, unknown>;
    w.__ERROR_REPORTER__ = errorReporter;
    w.__SHOW_ERRORS__ = () => {
      // eslint-disable-next-line no-console
      console.table(errorReporter.getErrors());
    };
    w.__CLEAR_ERRORS__ = () => {
      errorReporter.clearErrors();
      logger.debug('🧹 Errors cleared');
    };
    logger.debug('🛠️ Error debugging enabled. Use __SHOW_ERRORS__() and __CLEAR_ERRORS__() in console.');
  }
};